import * as Yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum DeSoiPurchaseOrderLineItemKeys {
  itemNumber = 'itemNumber',
  description = 'description',
  quantity = 'quantity',
  quantityInLbs = 'quantityInLbs', // Only for ingredient raw materials
  unitCost = 'unitCost',
  totalCost = 'totalCost',
}

export enum DeSoiPOType {
  PRODUCTION_RUN = 'PRODUCTION_RUN',
  RAW_MATERIAL = 'RAW_MATERIAL',
  RAW_MATERIAL_INGREDIENT = 'RAW_MATERIAL_INGREDIENT',
}

const DeSoiLineItemSchema = Yup.object().shape({
  [DeSoiPurchaseOrderLineItemKeys.itemNumber]: Yup.string().nullable().optional(),
  [DeSoiPurchaseOrderLineItemKeys.description]: Yup.string().nullable().optional(),
  [DeSoiPurchaseOrderLineItemKeys.quantity]: Yup.string().nullable().optional(),
  [DeSoiPurchaseOrderLineItemKeys.quantityInLbs]: Yup.string().nullable().optional(),
  [DeSoiPurchaseOrderLineItemKeys.unitCost]: Yup.string().nullable().optional(),
  [DeSoiPurchaseOrderLineItemKeys.totalCost]: Yup.string().nullable().optional(),
})

export const deSoiPurchaseOrderFileDataSchema = Yup.object().shape({
  organization: Yup.object()
    .shape({
      companyName: Yup.string().nullable().optional(),
      addressLine1: Yup.string().nullable().optional(),
      addressLine2: Yup.string().nullable().optional(),
      cityStateZip: Yup.string().nullable().optional(),
      phoneNumber: Yup.string().nullable().optional(),
      website: Yup.string().nullable().optional(),
    })
    .required(),
  billTo: Yup.object()
    .shape({
      companyName: Yup.string().nullable().optional(),
      addressLine1: Yup.string().nullable().optional(),
      addressLine2: Yup.string().nullable().optional(),
      cityStateZip: Yup.string().nullable().optional(),
      phoneNumber: Yup.string().nullable().optional(),
      website: Yup.string().nullable().optional(),
    })
    .required(),
  vendor: Yup.object()
    .shape({
      vendorName: Yup.string().nullable().optional(),
    })
    .required(),
  poType: Yup.string().nullable().optional(),
  purchaseOrderNumber: Yup.string().nullable().optional(),
  buyer: Yup.string().nullable().optional(),
  shipVia: Yup.string().nullable().optional(),
  deliveryDateReq: Yup.string().nullable().optional(),
  paymentTerms: Yup.string().nullable().optional(),
  date: Yup.string().nullable().optional(),
  deliveryDate: Yup.string().nullable().optional(),
  baseUnit: Yup.string().nullable().optional(),
  destination: Yup.object()
    .shape({
      company: Yup.string().nullable().optional(),
      addressLine1: Yup.string().nullable().optional(),
      addressLine2: Yup.string().nullable().optional(),
      cityStateZip: Yup.string().nullable().optional(),
      receivingHours: Yup.string().nullable().optional(),
      deliveryApptReq: Yup.string().nullable().optional(),
      contactName: Yup.string().nullable().optional(),
      contactFullName: Yup.string().nullable().optional(),
      contactEmail: Yup.string().nullable().optional(),
      contactPhone: Yup.string().nullable().optional(),
    })
    .required(),
  items: Yup.array().of(DeSoiLineItemSchema).required(),
  instruction1: Yup.string().nullable().optional(),
  instruction2: Yup.string().nullable().optional(),
})

export type DeSoiPurchaseOrderFileData = Yup.InferType<typeof deSoiPurchaseOrderFileDataSchema>
type RequiredFileData = MakeRequiredAndNullable<DeSoiPurchaseOrderFileData>

export const deSoiPurchaseOrderFileMappings: RequiredFileData = {
  organization: {
    companyName: '3293a2a5-94e8-414a-b807-7ac1fa6498df',
    addressLine1: 'c4a6618e-b976-4103-a094-b336de29d307',
    addressLine2: '3d19b8e4-c95c-4a73-993d-34c43b5b9b55',
    cityStateZip: '1fc66708-d250-40c0-9102-658aa0b62686',
    phoneNumber: '26d61acc-7de3-44e2-801a-e083a6331b16',
    website: 'b0f20fcf-401f-4d3e-b37b-045feaac9707',
  },
  billTo: {
    companyName: '42a2df5e-04b6-4a9b-8762-cc1c53442c99',
    addressLine1: 'ca681f3d-d8b2-4abd-a8a2-83e3be13be89',
    addressLine2: '1f875537-dee4-44e3-9e9c-d1c5cf686fc7',
    cityStateZip: '724dc4e1-09cf-47b6-969e-415d38970822',
    phoneNumber: '05c9a240-cd2a-4197-a338-dbb9864d5f1f',
    website: 'da632ec5-5090-4913-8d22-1d382bcf375f',
  },
  vendor: {
    vendorName: '44027e15-526e-40a5-9541-4c21db1d4dd2',
  },
  poType: 'a8c4b9e2-1d3f-4a5b-8c7e-2f6a9b4e8d1c',
  purchaseOrderNumber: 'fbd10853-f3c9-4b69-9049-d8d0381c305d',
  buyer: '1391b121-367f-44b9-8933-ba5b3ae5b744',
  shipVia: 'a947dba3-03f6-4ffb-a6ec-9c43ba4667c6',
  deliveryDateReq: 'ae05a65a-1b60-4b7c-bb31-02ab98ae0ec1',
  paymentTerms: 'c8639fa6-a21f-42e5-81e7-7c142bc51568',
  date: '64a71f72-d1a1-4be0-aa6d-51b874c001da',
  deliveryDate: '3279c76d-19d9-48ad-b022-d0f8d63b1b26',
  baseUnit: '5e6b1d14-6f47-4895-9b4f-1f8ecf5312f7',
  destination: {
    company: '032a3548-bd61-45cc-bba9-6dbf6bd27445',
    addressLine1: '2982aef1-0e20-4acc-b268-a236e346e8da',
    addressLine2: '9515e40a-1ec6-49b3-8cd9-ebd1336c753f',
    cityStateZip: '80163ab2-f566-45e1-99cc-f287714e56b5',
    receivingHours: 'c1f9f4e1-50a6-4b17-a4a5-b7ad7c5e6db9',
    deliveryApptReq: '92a4d2ab-32c6-4dcb-89d3-9d8e0cc45d64',
    contactName: '37611694-5f54-4fd1-bbac-b67e074b5fee',
    contactFullName: 'c87a40cd-138d-41cf-bebc-322ffb54cb2b',
    contactEmail: '58ea9e57-fad7-42da-8950-3acb4894cee4',
    contactPhone: '529d3e95-8815-42bc-a3c5-ec53c08d78f3',
  },
  instruction1: '58a58714-987f-49a6-a8a9-64cf9c074ed6',
  instruction2: '48b2e513-0f33-429a-9570-5df2b1c2da1f',
  items: [
    {
      itemNumber: '7eacfa72-0cd4-4ec9-a7b7-df40c8b6cf20',
      description: 'bc518fe9-5f42-492f-a7c5-10faf5695103',
      quantity: 'd259c7fc-fe09-41c7-8662-582c9de8083f',
      quantityInLbs: 'ac699cd6-3d25-436c-88b4-91f7c236872e',
      unitCost: '97553778-8863-434e-a1db-02ac2201e3e6',
      totalCost: null,
    },
  ],
}
